<template>
  <div>
    <!-- Otherwise the initial search after a new/incognito tab would refresh the page usually-->
    <v-form @submit.prevent>
      <v-row>
        <v-row class="pr-3">
          <v-col class="col-md-2 offset-md-10 text-right">
            <OnboardNewVendorDialogButton
              v-model="showDialog"
              @saved="loadTableData"
            />
          </v-col>
        </v-row>
      </v-row>
      <v-row class="">
        <v-col class="col-md-3">
          <v-text-field
            label="Business Name"
            v-model="searchFilters.businessName"
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
        <v-col class="col-md-3">
          <v-select
            label="Open Item"
            outlined
            dense
            :items="openItemOptions"
            item-text="name"
            item-value="programmaticName"
            clearable
            v-model="searchFilters.openItem"
            hide-details="auto"
          />
        </v-col>
        <v-col class="col-md-3">
          <v-text-field
            label="Email"
            v-model="searchFilters.email"
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
        <v-col class="col-md-3">
          <v-text-field
            label="Created By"
            v-model="searchFilters.createdBy"
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
      </v-row>
      <v-row class="">
        <v-col class="col-md-3">
          <v-select
            label="Status"
            outlined
            dense
            :items="statusText"
            clearable
            v-model="searchFilters.status"
            hide-details="auto"
          />
        </v-col>
        <v-col class="col-md-3">
          <v-text-field
            label="Assignee"
            v-model="searchFilters.assignee"
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
        <v-col class="col-md-3">
          <v-select
            label="Facility"
            outlined
            dense
            :items="facilities"
            item-text="name"
            item-value="id"
            clearable
            v-model="searchFilters.facilityId"
            hide-details="auto"
          />
        </v-col>
        <v-col class="col-md-3">
          <v-btn
            outlined
            type="submit"
            color="primary"
            class="col-md-6"
            @click="search()"
            :block="$vuetify.breakpoint.xsOnly"
          >
            Search
          </v-btn>
          <v-btn
            outlined
            color="primary"
            class="col-md-3 offset-md-3"
            @click="reset()"
            :block="$vuetify.breakpoint.xsOnly"
          >
            Reset
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-data-table
      :headers="headers"
      :items="computedItems"
      :expanded.sync="expanded"
      item-key="covgId"
      :server-items-length="itemsLength"
      show-expand
      :loading="dataTableLoading"
      loading-text="Loading... Please wait"
      :options.sync="searchOptions"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
    >
      <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
        <v-btn
          icon
          @click="expand(!isExpanded)"
          v-if="item.extraNames.length > 0"
        >
          <v-icon class="toggleUpDown" :class="{ rotate: isExpanded }">
            mdi-chevron-right
          </v-icon>
        </v-btn>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td></td>
        <td :colspan="headers.length - 1">
          <span class="ml-4">Other business names:</span>
          <v-chip v-for="name in item.extraNames" :key="name" class="ml-2">
            {{ name }}
          </v-chip>
        </td>
      </template>
      <template v-slot:item.assignee="{ value }">
        <text-and-hovertext
          :text="value.join(', ')"
          class="assignees"
        ></text-and-hovertext>
      </template>
      <template v-slot:item.createTime="{ item }">
        <span>{{
          new Date(
            item.createTime != null ? item.createTime : null
          ).toLocaleString()
        }}</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="actions d-flex float-right">
          <v-btn
            depressed
            color="primary"
            class="mx-1"
            :to="{
              name: 'workflow',
              params: {
                covgId: item.covgId
              }
            }"
          >
            View
          </v-btn>
          <v-btn
            depressed
            outlined
            color="primary"
            class="mx-1"
            @click="initiateDelete(item)"
          >
            Delete
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="deleteConfirmationDialogOpen" width="672">
      <v-card class="pa-4">
        <v-card-title class="justify-center pt-0">
          <v-icon size="48" color="colorAlertRed">mdi-alert-rhombus</v-icon>
          <v-btn icon absolute top right @click="cancelDelete">
            <v-icon>mdi-window-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          All onboarding information will be deleted. The vendor record will not
          be deleted and remain visible on the Vendors tab. Are you sure you
          wish to delete this vendor's onboarding information?
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDelete">
            Cancel
          </v-btn>

          <v-btn color="primary" @click="actuallyDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { _delete, get } from "@/services/request";
import {
  templateWorkflowSteps,
  workflowsAPI,
  workflowStepsAPI,
  subclientsForSelectedClientAPI
} from "@/services/api";
import hasNotifications from "@/mixins/has-notifications";
import OnboardNewVendorDialogButton from "@/views/client/OnboardNewVendorDialogButton";
import moment from "moment";
import TextAndHovertext from "@/components/TextAndHovertext.vue";

const statuses = [
  { text: "Blocked", value: "BLOCKED" },
  { text: "Pending", value: "PENDING" },
  { text: "Active", value: "ACTIVE" },
  { text: "Complete", value: "COMPLETE" }
];

export default {
  name: "OnboardingTable",
  components: { TextAndHovertext, OnboardNewVendorDialogButton },
  data() {
    return {
      expanded: [],
      headers: [
        { text: "Business Name", value: "preferredName", sortable: false },
        { text: "Open Item", value: "openItem", sortable: false },
        { text: "Email", value: "email" },
        { text: "Created By", value: "createdBy", sortable: false },
        { text: "Creation Date", value: "createTime" },
        { text: "Status", value: "status" },
        { text: "Assignee", value: "assignee", sortable: false },
        { text: "Days Old", value: "age" },
        { text: "Facility", value: "facility", sortable: false },
        { text: "", value: "actions", align: "end", sortable: false }
      ],
      statuses,
      rawItems: [],
      openItemOptions: [],
      facilities: [],
      itemsLength: 0,
      dataTableLoading: true,
      rowToDelete: {},
      deleteConfirmationDialogOpen: false,
      showDialog: false,
      searchFilters: {},
      searchOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      }
    };
  },
  created() {
    this.loadTableData();
    this.loadOpenItemOptions();
    this.loadFacilityData();
  },
  computed: {
    statusText() {
      return statuses.map(s => s.text);
    },
    computedItems() {
      return this.rawItems.map(item => ({
        ...item,
        age: moment().diff(item.createTime, "days")
      }));
    }
  },
  watch: {
    searchOptions: {
      handler() {
        this.loadTableData();
      },
      deep: true
    }
  },
  methods: {
    getFilters() {
      const filters = {};

      filters.businessName = this.searchFilters.businessName;
      filters.email = this.searchFilters.email;
      filters.createdBy = this.searchFilters.createdBy;
      filters.facilityId = this.searchFilters.facilityId;
      filters.assignee = this.searchFilters.assignee;

      if (this.searchFilters.status)
        filters.status = this.searchFilters.status.toUpperCase();
      if (this.searchFilters.openItem) {
        if (this.searchFilters.openItem === "ASSIGNED_TO_ME") {
          filters.showAssignedToMe = true;
        } else {
          filters.openItemProgrammaticName = this.searchFilters.openItem;
        }
      }

      return filters;
    },
    loadTableData() {
      this.dataTableLoading = true;
      get(workflowsAPI, {
        page: this.searchOptions.page - 1, //vuetify uses 1-indexed page, server uses 0-indexed
        size: this.searchOptions.itemsPerPage,
        sortBy: this.searchOptions.sortBy.at(0),
        sortDesc: this.searchOptions.sortDesc.at(0),
        ...this.getFilters()
      }).then(response => {
        if (response && response.data) {
          this.itemsLength = response.data.total;
          this.rawItems = response.data.content;
        }
        this.dataTableLoading = false;
      });
    },
    reset() {
      this.searchFilters = {};
      this.search();
    },
    search() {
      this.searchOptions.page = 1;
      this.loadTableData();
    },
    actuallyDelete() {
      this.deleteConfirmationDialogOpen = false;
      let row = this.rowToDelete;
      this.rowToDelete = null;
      _delete(workflowStepsAPI.getUrl({ covgId: row.covgId })).then(() => {
        this.rawItems = this.rawItems.filter(it => it !== row);
        this.showMessage(
          `The onboarding information for ${row.preferredName} was successfully deleted.`
        );
      });
    },
    initiateDelete(row) {
      this.deleteConfirmationDialogOpen = true;
      this.rowToDelete = row;
    },
    cancelDelete() {
      this.deleteConfirmationDialogOpen = false;
      this.rowToDelete = null;
    },
    loadOpenItemOptions() {
      get(templateWorkflowSteps).then(response => {
        if (response && response.data) {
          this.openItemOptions = response.data;
          this.openItemOptions.unshift({
            programmaticName: "ASSIGNED_TO_ME",
            name: "Open Items Assigned to Me"
          });
        }
      });
    },
    loadFacilityData() {
      get(subclientsForSelectedClientAPI).then(response => {
        if (response && response.data) {
          this.facilities = response.data;
        }
      });
    }
  },
  mixins: [hasNotifications]
};
</script>

<style lang="scss" scoped>
.toggleUpDown {
  transition: transform 0.3s ease-in-out !important;
}

.toggleUpDown.rotate {
  transform: rotate(90deg);
}

.assignees {
  max-width: 200px;
}
</style>
